/**
 * How much height the user can actually SEE, which inside this app is not
 * `100vh`.
 *
 * The app runs as an RDrive dashboard iframe. The shell (SnagR-Web
 * `IframeLayout`) copies the dashboard's configured `elementAttributes` onto
 * the <iframe>, so its height is whatever the dashboard author typed — in
 * practice far taller than the screen, so the embedded app never shows an
 * inner scrollbar and the shell page scrolls instead. Inside that iframe
 * `100vh` / `window.innerHeight` is the IFRAME's height, not the screen's: a
 * panel sized `calc(100vh - x)` grows to fill it and pushes whatever sits
 * below it (the Save Tasks bar) hundreds of pixels past the bottom of the
 * visible screen, reachable only by zooming the browser out.
 *
 * `window.screen.availHeight` is independent of the iframe and scales with
 * browser zoom, so it bounds the iframe height when the iframe is the taller
 * of the two. Outside an iframe (dev server) `innerHeight` is the real
 * viewport and wins. The same problem is handled the same way for dialogs —
 * see the `.save-tasks-dialog` rule in ReviewStructureStep.vue.
 */

// Screen height the user cannot use: browser tab strip + address bar, plus the
// RDrive shell's own header and dashboard tab bar above the iframe.
export const BROWSER_CHROME_ALLOWANCE = 200;

// Never report less than this, however small availHeight claims to be.
export const MIN_VISIBLE_VIEWPORT_HEIGHT = 480;

const readWindowValue = (getter) => {
  try {
    return typeof window === 'undefined' ? 0 : (getter() || 0);
  } catch {
    return 0;
  }
};

/**
 * Visible height in CSS pixels.
 * Options (all optional, mainly for tests): innerHeight, screenHeight,
 * chromeAllowance.
 */
export function getVisibleViewportHeight(options = {}) {
  const innerHeight = options.innerHeight ?? readWindowValue(() => window.innerHeight);
  const screenHeight = options.screenHeight ?? readWindowValue(() => window.screen?.availHeight);
  const chromeAllowance = options.chromeAllowance ?? BROWSER_CHROME_ALLOWANCE;

  if (!screenHeight) return innerHeight;
  const screenBudget = Math.max(MIN_VISIBLE_VIEWPORT_HEIGHT, screenHeight - chromeAllowance);
  if (!innerHeight) return screenBudget;
  return Math.min(innerHeight, screenBudget);
}

/**
 * Height for a panel that should fill what is left of the visible screen.
 *
 * `top` is the panel's viewport-relative top (getBoundingClientRect().top),
 * which accounts for everything rendered above it — app bar, stepper header,
 * import-note alerts, toolbar — without hard-coding any of their heights.
 * `reserveBelow` is what must stay visible underneath it (the actions bar and
 * the page's bottom padding).
 */
export function fitHeightToVisibleViewport({
  top = 0,
  reserveBelow = 0,
  minHeight = 0,
  ...viewportOptions
} = {}) {
  const available = getVisibleViewportHeight(viewportOptions) - Math.max(top, 0) - reserveBelow;
  return Math.max(minHeight, Math.round(available));
}
